import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText, MessageSquare, LogOut, RefreshCw, ChevronDown, ChevronUp, Clock, CheckCircle, AlertCircle, Inbox, Settings, DollarSign, ExternalLink, Trash2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });
ax.interceptors.request.use((config) => {
  const token = localStorage.getItem("ng_admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const statusColors = {
  new: "bg-[#FF5722] text-white",
  "pricing-required": "bg-yellow-600 text-white",
  draft: "bg-blue-600 text-white",
  sent: "bg-purple-600 text-white",
  approved: "bg-green-600 text-white",
  declined: "bg-red-700 text-white",
  "in-production": "bg-teal-600 text-white",
  reviewed: "bg-blue-600 text-white",
  quoted: "bg-green-600 text-white",
  closed: "bg-[#666] text-white",
};

const STATUSES = ["new", "pricing-required", "draft", "sent", "approved", "declined", "in-production"];
const statusIcon = { new: AlertCircle, "pricing-required": DollarSign, draft: FileText, sent: ExternalLink, approved: CheckCircle, declined: Inbox, "in-production": Clock, reviewed: Clock, quoted: CheckCircle, closed: Inbox };

function formatDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-[#141414] border border-[#2E2E2E] p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-[#A3A3A3] uppercase tracking-wider font-semibold">{label}</span>
        <div className={`w-8 h-8 flex items-center justify-center ${accent ? "bg-[#FF5722]/10" : "bg-[#1F1F1F]"}`}>
          <Icon size={16} className={accent ? "text-[#FF5722]" : "text-[#A3A3A3]"} />
        </div>
      </div>
      <p className="text-2xl font-bold" style={{ fontFamily: "'Clash Display', sans-serif" }}>{value}</p>
    </div>
  );
}

function QuoteRow({ q, onStatusChange, onDelete }) {
  const S = statusIcon[q.status] || AlertCircle;
  const hasPricing = !!q.calculated_pricing;
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="border border-[#2E2E2E] bg-[#141414] mb-2">
      <div className="flex items-center gap-4 p-4 hover:bg-[#1F1F1F] transition-colors">
        <S size={14} className={q.status === "new" ? "text-[#FF5722]" : "text-[#A3A3A3]"} />
        <Link to={`/admin/quotes/${q._id}`} className="flex-1 min-w-0 group">
          <p className="text-sm font-bold text-white truncate group-hover:text-[#FF5722] transition-colors">
            {q.name} {q.company ? `- ${q.company}` : ""}
            {(q.items?.length || 0) > 1 && <span className="text-[#FF5722] text-xs ml-2">({q.items.length} items)</span>}
          </p>
          <p className="text-xs text-[#A3A3A3] truncate">{(q.product_type || "").replace(/-/g, " ")} | {q.email}</p>
        </Link>
        {hasPricing && (
          <span className="text-xs text-white font-medium hidden sm:inline">
            ${(q.calculated_pricing?.summary?.total || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
          </span>
        )}
        <Badge className={`text-[10px] px-2 py-0.5 rounded-none ${statusColors[q.status] || statusColors.new}`}>
          {q.status || "new"}
        </Badge>
        <span className="text-xs text-[#666] hidden sm:inline">{formatDate(q.created_at)}</span>
        <Select defaultValue={q.status || "new"} onValueChange={(val) => onStatusChange(q._id, val)}>
          <SelectTrigger className="w-28 h-7 bg-[#1F1F1F] border-[#2E2E2E] text-white text-[10px] rounded-none">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1F1F1F] border-[#2E2E2E]">
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s} className="text-white text-xs hover:bg-[#2E2E2E] focus:bg-[#2E2E2E] focus:text-white">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {confirmDelete ? (
          <div className="flex items-center gap-1">
            <button
              data-testid={`quote-delete-confirm-${q._id}`}
              onClick={() => { onDelete(q._id); setConfirmDelete(false); }}
              className="px-2 py-1 bg-red-600 text-white text-[10px] font-semibold hover:bg-red-700 transition-colors"
            >
              Delete
            </button>
            <button
              data-testid={`quote-delete-cancel-${q._id}`}
              onClick={() => setConfirmDelete(false)}
              className="px-2 py-1 border border-[#2E2E2E] text-[#A3A3A3] text-[10px] hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            data-testid={`quote-delete-${q._id}`}
            onClick={() => setConfirmDelete(true)}
            className="p-1.5 text-[#666] hover:text-red-400 transition-colors"
            title="Delete quote"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function ContactRow({ c, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const S = statusIcon[c.status] || AlertCircle;

  return (
    <div className="border border-[#2E2E2E] bg-[#141414] mb-2">
      <button
        data-testid={`contact-row-${c._id}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-[#1F1F1F] transition-colors"
      >
        <S size={14} className={c.status === "new" ? "text-[#FF5722]" : "text-[#A3A3A3]"} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white truncate">{c.name} {c.subject ? `— ${c.subject}` : ""}</p>
          <p className="text-xs text-[#A3A3A3] truncate">{c.email}</p>
        </div>
        <Badge className={`text-[10px] px-2 py-0.5 rounded-none ${statusColors[c.status] || statusColors.new}`}>
          {c.status || "new"}
        </Badge>
        <span className="text-xs text-[#666] hidden sm:inline">{formatDate(c.created_at)}</span>
        {open ? <ChevronUp size={14} className="text-[#666]" /> : <ChevronDown size={14} className="text-[#666]" />}
      </button>
      {open && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="border-t border-[#2E2E2E] p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-4">
            <div><span className="text-[#666]">Phone: </span><span className="text-white">{c.phone || "-"}</span></div>
            <div><span className="text-[#666]">Subject: </span><span className="text-white">{c.subject || "-"}</span></div>
          </div>
          <div className="mb-4"><span className="text-[#666] text-sm">Message:</span><p className="text-white text-sm mt-1 whitespace-pre-wrap">{c.message}</p></div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#666]">Status:</span>
            <Select defaultValue={c.status || "new"} onValueChange={(val) => onStatusChange(c._id, val)}>
              <SelectTrigger data-testid={`contact-status-${c._id}`} className="w-32 h-8 bg-[#1F1F1F] border-[#2E2E2E] text-white text-xs rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1F1F1F] border-[#2E2E2E]">
                {["new", "reviewed", "quoted", "closed"].map((s) => (
                  <SelectItem key={s} value={s} className="text-white text-xs hover:bg-[#2E2E2E] focus:bg-[#2E2E2E] focus:text-white">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default function AdminDashboardPage() {
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [q, c, s] = await Promise.all([
        ax.get("/admin/quotes"),
        ax.get("/admin/contacts"),
        ax.get("/admin/stats"),
      ]);
      setQuotes(q.data);
      setContacts(c.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) { navigate("/admin/login"); return; }
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [navigate]); // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleQuoteStatus = async (id, status) => {
    try {
      await ax.patch(`/admin/quotes/${id}`, { status });
      setQuotes((prev) => prev.map((q) => (q._id === id ? { ...q, status } : q)));
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  const handleContactStatus = async (id, status) => {
    try {
      await ax.patch(`/admin/contacts/${id}`, { status });
      setContacts((prev) => prev.map((c) => (c._id === id ? { ...c, status } : c)));
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  const handleDeleteQuote = async (id) => {
    try {
      await ax.delete(`/admin/quotes/${id}`);
      setQuotes((prev) => prev.filter((q) => q._id !== id));
      toast.success("Quote deleted");
    } catch { toast.error("Delete failed"); }
  };

  const handleLogout = async () => {
    await ax.post("/auth/logout");
    localStorage.removeItem("ng_admin_token");
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Clash Display', sans-serif" }}>
              Admin Dashboard
            </h1>
            <p className="text-sm text-[#A3A3A3] mt-1">Manage quote requests and contact enquiries</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/pricing" data-testid="pricing-settings-link" className="flex items-center gap-2 px-4 py-2 border border-[#2E2E2E] text-[#A3A3A3] text-sm hover:border-[#FF5722] hover:text-[#FF5722] transition-colors">
              <Settings size={14} /> Pricing
            </Link>
            <button data-testid="admin-refresh-btn" onClick={fetchData} className="p-2 border border-[#2E2E2E] text-[#A3A3A3] hover:border-[#FF5722] hover:text-[#FF5722] transition-colors">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button data-testid="admin-logout-btn" onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 border border-[#2E2E2E] text-[#A3A3A3] text-sm hover:border-red-500 hover:text-red-400 transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard label="New Quotes" value={stats.new_quotes || 0} icon={AlertCircle} accent />
          <StatCard label="Pricing Required" value={stats.pricing_required || 0} icon={DollarSign} accent />
          <StatCard label="Draft Quotes" value={stats.draft_quotes || 0} icon={FileText} />
          <StatCard label="Sent" value={stats.sent_quotes || 0} icon={ExternalLink} />
          <StatCard label="Total Quotes" value={stats.total_quotes || 0} icon={FileText} />
          <StatCard label="Total Messages" value={stats.total_contacts || 0} icon={MessageSquare} />
          <StatCard label="New Messages" value={stats.new_contacts || 0} icon={AlertCircle} accent />
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mb-6 border-b border-[#2E2E2E]">
          <button
            data-testid="tab-quotes"
            onClick={() => setTab("quotes")}
            className={`px-6 py-3 text-sm font-semibold transition-colors ${
              tab === "quotes" ? "text-[#FF5722] border-b-2 border-[#FF5722]" : "text-[#A3A3A3] hover:text-white"
            }`}
          >
            Quotes ({quotes.length})
          </button>
          <button
            data-testid="tab-contacts"
            onClick={() => setTab("contacts")}
            className={`px-6 py-3 text-sm font-semibold transition-colors ${
              tab === "contacts" ? "text-[#FF5722] border-b-2 border-[#FF5722]" : "text-[#A3A3A3] hover:text-white"
            }`}
          >
            Messages ({contacts.length})
          </button>
        </div>

        {/* Content */}
        {loading && (
          <div className="text-center py-16 text-[#A3A3A3]">Loading...</div>
        )}
        {!loading && tab === "quotes" && quotes.length === 0 && (
          <div className="text-center py-16 text-[#A3A3A3]"><Inbox size={32} className="mx-auto mb-3 opacity-50" /><p>No quotes yet</p></div>
        )}
        {!loading && tab === "quotes" && quotes.length > 0 && (
          quotes.map((q) => <QuoteRow key={q._id} q={q} onStatusChange={handleQuoteStatus} onDelete={handleDeleteQuote} />)
        )}
        {!loading && tab === "contacts" && contacts.length === 0 && (
          <div className="text-center py-16 text-[#A3A3A3]"><Inbox size={32} className="mx-auto mb-3 opacity-50" /><p>No messages yet</p></div>
        )}
        {!loading && tab === "contacts" && contacts.length > 0 && (
          contacts.map((c) => <ContactRow key={c._id} c={c} onStatusChange={handleContactStatus} />)
        )}
      </div>
    </div>
  );
}
